import {
  rootRouteId,
  type AnyRoute,
  type AnyRouter,
} from '@tanstack/react-router';

const cache = new WeakMap<object, readonly AnyRoute[]>();

/**
 * Every route in the router's tree except the root, in tree order. Stands in
 * for `router.flatRoutes`, which TanStack Router removed. The list is cached
 * per `routesById` so identity-keyed memos downstream stay stable.
 */
export function getFlatRoutes(
  router: Pick<AnyRouter, 'routesById'>,
): readonly AnyRoute[] {
  const byId = router.routesById;
  let routes = cache.get(byId);
  if (!routes) {
    routes = Object.values(byId).filter((route) => route.id !== rootRouteId);
    cache.set(byId, routes);
  }
  return routes;
}

/**
 * The route the router renders for `pathname` (`/x/123` → `/x/$id`), or
 * undefined when no route matches the whole path.
 */
export function findExactRoute(
  router: Pick<AnyRouter, 'getMatchedRoutes'>,
  pathname: string,
): AnyRoute | undefined {
  const [, rawParams, route] = router.getMatchedRoutes(pathname);
  // A fuzzy match covers only a prefix of the path; `**` holds the rest.
  return route && !('**' in rawParams) ? route : undefined;
}
